import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogTitle, FormControlLabel, FormGroup, IconButton, Input, InputAdornment, Switch, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { capitalize } from "../../utils/helpers";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet-async";
import ApiService from "../../services/categoryManagementApi";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import TableFilter from "../../components/shared/TableFilter";
import CustomLoader from "../../components/customLoader";
import AddLanguage from "./Add";

const { LanguageApi } = ApiService;

export default function LanguageList() {
  const navigate = useNavigate();

  const [searchItem, setSearchItem] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [addId, setAddId] = useState("");

  const handleClose = () => setShow(false);

  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => capitalize(row.name),
    },
    {
      name: "Status",
      cell: (row) => {
        const activeLanguages = data.filter((lang) => lang.status === 1);
        const isLastActiveLanguage = activeLanguages.length === 1 && row.status === 1;
    
        return (
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={row.status === 1}
                  onClick={() => {
                    if (!isLastActiveLanguage) {
                      setShow(true);
                      setCurrentStatus(row.status);
                      setStatusId(row._id);
                      setAction("status");
                      setWarning(
                        `Are you sure you want to ${
                          row.status === 1 ? "deactivate" : "activate"
                        } the language?`
                      );
                    }
                  }}
                  disabled={isLastActiveLanguage}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </FormGroup>
        );
      },
    },    
    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          {/* <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/customer-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip> */}
          {/* <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {setShowAddPopup(true);
                setAddId(row?._id)
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip> */}
          <Tooltip title="Delete" placement="right">
            <IconButton color="primary" onClick={() => deleteData(row?._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const fetchLanguageData = () => {
    const body = {
      searchItem: searchItem ? searchItem.trim() : "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };

    LanguageApi
      .getAll(body)
      .then((res) => {
        if (res.status === 200) {
          setData(res?.data?.data?.result || []);
          setTotalRecords(res?.data?.data?.totalRecords || 0);
        }
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true)
    fetchLanguageData();
  }, [searchItem, currentPage, pageSize, refresh, status]);

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete the language?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await LanguageApi.delete({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setRefresh(!refresh);
            setData((prevData) => prevData.filter((item) => item._id !== id));
  
            setTotalRecords((prevTotal) => prevTotal - 1);
  
            if ((data.length - 1) === 0 && currentPage > 1) {
              setCurrentPage(currentPage - 1);
            }
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };
  

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus ? 0 : 1;
      const res = await LanguageApi.changeStatus({ id, status: newStatus });
      if (res?.status === 200) {
        toast.success(res?.data?.message);
        setRefresh(!refresh);
        setShow(false);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  return (
    <Card>
      <Helmet>
        <title>Language Management</title>
      </Helmet>
      <CardHeader title="Language Management" titleTypographyProps={{ variant: "h4" }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <TableFilter data={STATUS_OPTIONS_LIST} value={status} setValue={setStatus} filterName="Status" />
        <Input
          placeholder="Search"
          value={searchItem}
          onChange={(e) => setSearchItem(e.target.value.trimStart())}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setSearchItem("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {/* <Button
          variant="contained"
          sx={{ ml: 3, mr: 3 }}
          onClick={() => setShowAddPopup(true)}
        >
          Add
        </Button> */}
      </Box>
      <CardContent>
        <DataTable
         customStyles={{
          headCells: {
            style: {
              fontSize: "15px",
              fontWeight: "bold",
            },
          },
        }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalRecords}
          onChangePage={(page) => setCurrentPage(page)}
          paginationPerPage={pageSize}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(1);
          }}
          progressPending={loading}
          progressComponent={<CustomLoader />}
        />
      </CardContent>
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={() => toggleChecked(statusId, currentStatus)}>
            Yes
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            No
          </Button>
        </DialogActions>
      </Dialog>

      {showAddPopup ? (
        <AddLanguage
          show={showAddPopup}
          onHide={() => setShowAddPopup(false)}
          id={addId}
          fetchLanguageData={fetchLanguageData}
        />
      ) : (
        ""
      )}
    </Card>
  );
}
